import React from 'react';
import {
  Plus, ArrowRight, UserCheck, Link2, FolderOpen, GitBranch, MessageSquare,
} from 'lucide-react';
import type { IssueHistoryEntry } from '../../utils/issueHistory';

const TYPE_CONFIG: Record<string, { color: string; icon: React.FC<{ size?: number }>; label: string }> = {
  created: { color: 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400', icon: Plus, label: 'Criação' },
  status_changed: { color: 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400', icon: ArrowRight, label: 'Status' },
  assigned: { color: 'border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400', icon: UserCheck, label: 'Atribuição' },
  action_linked: { color: 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400', icon: Link2, label: 'Ação' },
  opportunity_linked: { color: 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400', icon: Link2, label: 'Oportunidade' },
  efficacy_check: { color: 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400', icon: ArrowRight, label: 'Eficácia' },
  reopened: { color: 'border-orange-500 bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400', icon: ArrowRight, label: 'Reabertura' },
  project_linked: { color: 'border-cyan-500 bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-400', icon: FolderOpen, label: 'Projeto' },
  workflow_linked: { color: 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-400', icon: GitBranch, label: 'Workflow' },
  note: { color: 'border-slate-400 bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400', icon: MessageSquare, label: 'Nota' },
};

function initials(name?: string): string {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

interface ProblemTimelineProps {
  entries: IssueHistoryEntry[];
  compact?: boolean;
  limit?: number;
  onViewAll?: () => void;
}

export const ProblemTimeline: React.FC<ProblemTimelineProps> = ({
  entries,
  compact = false,
  limit,
  onViewAll,
}) => {
  const sorted = [...entries].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  const visible = limit ? sorted.slice(0, limit) : sorted;

  if (visible.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-300 px-4 py-5 text-center dark:border-slate-600">
        <p className="text-[11px] text-slate-500">Nenhum evento de rastreabilidade registrado.</p>
        <p className="mt-1 text-[10px] text-slate-400">Transições de status, atribuições e vínculos aparecerão aqui.</p>
      </div>
    );
  }

  return (
    <div>
      {!compact && (
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Linha do tempo de governança</p>
          <span className="text-[10px] text-slate-400">{sorted.length} evento{sorted.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      <div className="relative">
        <div className="absolute bottom-2 left-[15px] top-2 w-px bg-slate-200 dark:bg-slate-700" />

        <div className="space-y-0">
          {visible.map(entry => {
            const d = new Date(entry.at);
            const dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
            const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const cfg = TYPE_CONFIG[entry.type] ?? TYPE_CONFIG.note;
            const Icon = cfg.icon;

            return (
              <div key={entry.id} className="relative flex gap-3 py-2.5 pl-0">
                <div className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${cfg.color}`}>
                  <Icon size={12} />
                </div>
                <div className="min-w-0 flex-1 border-b border-slate-100 pb-2.5 last:border-0 dark:border-slate-800">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-500 dark:bg-slate-800">
                      {cfg.label}
                    </span>
                    <span className="text-[10px] font-medium text-slate-500">{dateStr}</span>
                    <span className="text-[10px] text-slate-400">{timeStr}</span>
                  </div>
                  <p className="mt-1 text-[12px] font-medium leading-snug text-slate-800 dark:text-slate-200">
                    {entry.message}
                  </p>
                  {entry.actor_name && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-[8px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        {initials(entry.actor_name)}
                      </span>
                      <span className="text-[10px] text-slate-400">{entry.actor_name}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {onViewAll && sorted.length > (limit ?? 0) && (
        <button type="button" onClick={onViewAll} className="mt-2 text-[11px] font-medium text-blue-600 hover:underline">
          Ver histórico completo ({sorted.length} eventos)
        </button>
      )}
    </div>
  );
};

export default ProblemTimeline;
